import React, { useCallback } from 'react';
import { Box, Paper, Stack } from '@mui/material';
import { FormProvider, useWatch } from 'react-hook-form';
import { useQuestionManagement } from '../hooks';
import QuestionFieldList from './QuestionFieldList';
import QuestionTipsCard from '../QuestionTipsCard';
import QuestionsEditorHeader from './QuestionsEditorHeader';
import QualityScoreFormulaSelector from './QualityScoreFormulaSelector';
import FormulaBuilderModal from '@/components/campaigns/FormulaBuilder/FormulaBuilderModal';
import { useCampaignQuestionsEditor } from '../context/CampaignQuestionsEditorContext';
import { QuestionType } from '../types';

import type { CampaignQuestion, FormulaQuestion } from '../types';

interface QuestionsEditorFormProps {
    campaignId: number | null;
    initialQuestions: CampaignQuestion[];
    onOpenCopyDialog: () => void;
    onCancel: () => void;
}

/**
 * Form component for editing campaign questions.
 */
const QuestionsEditorForm: React.FC<QuestionsEditorFormProps> = (props) => {
    const { campaignId, initialQuestions, onOpenCopyDialog, onCancel } = props;

    const {
        fields,
        questionsToRemove,
        errors,
        isLoading,
        handleAddQuestion,
        handleQuestionDelete,
        handleQuestionReorder,
        handleSave,
        formMethods,
    } = useQuestionManagement({
        campaignId,
        initialQuestions,
        onSaveSuccess: onCancel,
    });

    const { openQuestionIndex, closeModal } = useCampaignQuestionsEditor();

    // Watch all questions for modal data
    const allQuestions = (useWatch({
        control: formMethods.control,
        name: 'questions',
    }) || []) as CampaignQuestion[];

    // Get the current question being edited
    const currentQuestion = openQuestionIndex !== null ? allQuestions[openQuestionIndex] : null;

    const handleSaveFormula = useCallback(
        (formula: string) => {
            if (openQuestionIndex === null) return;

            const updatedQuestion = { ...currentQuestion, formula } as FormulaQuestion;

            formMethods.setValue(`questions.${openQuestionIndex}` as const, updatedQuestion, {
                shouldValidate: false,
                shouldDirty: true,
            });

            closeModal();
        },
        [openQuestionIndex, currentQuestion, formMethods, closeModal]
    );

    const isFormulaModalOpen = currentQuestion?.type === QuestionType.FORMULA;

    return (
        <FormProvider {...formMethods}>
            <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', lg: 'row' } }} data-testid="campaign-questions-editor">
                <Box sx={{ flex: 1, order: { xs: 2, lg: 1 } }}>
                    <Paper sx={{ p: 2, pb: 1 }}>
                        <QuestionsEditorHeader
                            title="Edit project questions"
                            questionsExist={initialQuestions.length > 0}
                            onCancel={onCancel}
                            onCopyQuestions={onOpenCopyDialog}
                            handleAddQuestion={handleAddQuestion}
                        />
                        <QuestionFieldList
                            fields={fields}
                            questionsToRemove={questionsToRemove}
                            onQuestionDelete={handleQuestionDelete}
                            onQuestionReorder={handleQuestionReorder}
                            handleAddQuestion={handleAddQuestion}
                            handleSave={handleSave}
                            errors={errors}
                            isLoading={isLoading}
                        />
                    </Paper>
                </Box>
                <Box sx={{ width: { xs: '100%', lg: '300px' }, order: { xs: 1, lg: 2 } }}>
                    <Stack
                        spacing={2}
                        sx={{
                            position: { xs: 'static', lg: 'sticky' },
                            top: { lg: 16 },
                        }}
                    >
                        <QualityScoreFormulaSelector />
                        <Box sx={{ display: { xs: 'none', lg: 'block' } }}>
                            <QuestionTipsCard viewMode={false} />
                        </Box>
                    </Stack>
                </Box>
            </Box>
            {isFormulaModalOpen && currentQuestion && (
                <FormulaBuilderModal
                    open={isFormulaModalOpen}
                    onClose={closeModal}
                    questions={allQuestions}
                    initialFormula={(currentQuestion as FormulaQuestion).formula}
                    onSave={handleSaveFormula}
                />
            )}
        </FormProvider>
    );
};

export default QuestionsEditorForm;
